import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { Wrench, AlertTriangle, AlertCircle, Info, Loader2, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_CONFIG = {
  critical: { 
    color: "bg-red-500", 
    textColor: "text-red-500",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/50",
    icon: AlertTriangle,
    label: "Crítico"
  },
  warning: { 
    color: "bg-amber-500", 
    textColor: "text-amber-500",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/50",
    icon: AlertCircle,
    label: "Aviso"
  },
  info: { 
    color: "bg-blue-500", 
    textColor: "text-blue-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/50",
    icon: Info,
    label: "Informação"
  },
};

export default function MoldMaintenance() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/molds/maintenance-alerts`);
      setAlerts(response.data.alerts || []);
    } catch (error) {
      toast.error("Erro ao carregar alertas de manutenção");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const criticalCount = alerts.filter(a => a.alert_level === "critical").length;
  const warningCount = alerts.filter(a => a.alert_level === "warning").length;
  const infoCount = alerts.filter(a => a.alert_level === "info").length;

  return (
    <div className="space-y-6" data-testid="mold-maintenance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight">
            Manutenção Preventiva de Moldes
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitorização da vida útil dos moldes
          </p>
        </div>
        <Button variant="outline" onClick={fetchAlerts} data-testid="refresh-maintenance">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={`border-red-500/50 ${criticalCount > 0 ? "bg-red-500/10" : ""}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Críticos</p>
                <p className="font-chivo text-4xl font-bold text-red-500">{criticalCount}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className={`border-amber-500/50 ${warningCount > 0 ? "bg-amber-500/10" : ""}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avisos</p>
                <p className="font-chivo text-4xl font-bold text-amber-500">{warningCount}</p>
              </div>
              <AlertCircle className="h-10 w-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Informações</p>
                <p className="font-chivo text-4xl font-bold text-blue-500">{infoCount}</p>
              </div>
              <Info className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts List */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Alertas de Manutenção ({alerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length > 0 ? (
            <div className="space-y-4">
              {alerts.map((alert, index) => {
                const config = ALERT_CONFIG[alert.alert_level];
                const IconComponent = config.icon;
                
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-sm border ${config.borderColor} ${config.bgColor}`}
                    data-testid={`alert-${alert.mold.code}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-sm ${config.color}`}>
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-chivo font-bold text-lg">{alert.mold.code}</h3>
                            <Badge className={config.color}>{config.label}</Badge>
                          </div>
                          <p className="text-muted-foreground">{alert.message}</p>
                          {alert.part && (
                            <p className="text-sm">
                              Peça: <span className="font-medium">{alert.part.code} - {alert.part.description}</span>
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                            <span>Cavidades: {alert.mold.cavities}</span>
                            <span>•</span>
                            <span>Ciclo: {alert.mold.cycle_time}s</span>
                            <span>•</span>
                            <span>Canal: {alert.mold.runner_type === "hot" ? "Quente" : "Frio"}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right min-w-[150px]">
                        <p className={`font-mono text-2xl font-bold ${config.textColor}`}>
                          {alert.life_percentage}%
                        </p>
                        <p className="text-sm text-muted-foreground">vida utilizada</p>
                      </div>
                    </div>
                    
                    {/* Life Progress Bar */}
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Ciclos: {alert.mold.current_cycles.toLocaleString()}</span>
                        <span className="text-muted-foreground">Vida útil: {alert.mold.mold_life.toLocaleString()}</span>
                      </div>
                      <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                        <div
                          className={`h-full ${config.color}`}
                          style={{ width: `${Math.min(alert.life_percentage, 100)}%` }}
                        />
                      </div>
                      <p className="text-sm text-center">
                        <span className="font-mono font-bold">{alert.remaining_cycles.toLocaleString()}</span> ciclos restantes
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <Wrench className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Todos os moldes em bom estado</p>
              <p className="text-sm">Nenhum alerta de manutenção</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
